import React, { useState, useEffect } from 'react';
import { IDSButton, IDSInput, IDSCard, IDSAlert } from '@inera/ids-react';
import { saveTestResultComment, updateTestResultComment, deleteTestResultComment } from '../../services/testResultsAPI';
import { useAuth } from '../../contexts/AuthContext';
import CommentAvatar from './CommentAvatar';
import ConfirmDialog from '../ConfirmDialog';
import { formatDateTimeNoSeconds } from '../../utils/dateTime';

const isReviewerComment = (c) =>
  c.commentType === 'REVIEWER_COMMENT' || (c.authorRole && c.authorRole !== 'TESTER');

/**
 * Comments/justifications for a test result. Displayed as message bubbles
 * (IDSCard + CommentAvatar) that can be collapsed/expanded – identical look to
 * the self-declaration question comments.
 */
const CommentThread = ({ testResultId, initialJustification, userRole, existingComments = [], canAdd = false, onCommentsChanged }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState(existingComments);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCommentField, setShowCommentField] = useState(false);
  const [open, setOpen] = useState(true);
  // Comment currently being edited, and its draft text.
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [rowError, setRowError] = useState(null);
  // Comment pending delete confirmation.
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Update comments when existingComments prop changes
  useEffect(() => {
    setComments(existingComments);
  }, [existingComments]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      setLoading(true);
      setError(null);

      const result = await saveTestResultComment(testResultId, newComment);

      // The endpoint returns the whole updated TestResultDTO (with a fully
      // mapped comments list, authorName included), not just the new comment
      // — use it as-is instead of guessing the new comment's fields.
      if (result && Array.isArray(result.comments)) {
        setComments(result.comments);
        // Report state (used e.g. by the "answer every comment" resubmit check)
        // otherwise never learns about this until the page is reloaded.
        onCommentsChanged?.(result.comments);
      }

      setNewComment('');
      setShowCommentField(false);
    } catch (err) {
      console.error('Error adding comment:', err);
      setError('Kunde inte spara kommentar');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => formatDateTimeNoSeconds(dateString);

  const roleLabelFor = (c) => (isReviewerComment(c) ? 'Granskare' : 'Testare');

  // Only the comment's own author may edit/remove it, and only while it's still
  // eligible (backend-computed: author's active phase + still in the open round).
  const canEdit = (c) => canAdd && !!user?.id && c.authorId === user.id && c.stillEditable;

  const startEdit = (c) => {
    setRowError(null);
    setEditingId(c.id);
    setEditText(c.commentText);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
    setRowError(null);
  };

  const saveEdit = async (commentId) => {
    if (!editText.trim()) return;
    try {
      setRowError(null);
      const result = await updateTestResultComment(testResultId, commentId, editText.trim());
      if (result && Array.isArray(result.comments)) {
        setComments(result.comments);
        onCommentsChanged?.(result.comments);
      }
      cancelEdit();
    } catch (err) {
      console.error('Error updating comment:', err);
      setRowError('Kunde inte spara ändringen');
    }
  };

  const handleDelete = async (commentId) => {
    setConfirmDeleteId(null);
    try {
      setRowError(null);
      const result = await deleteTestResultComment(testResultId, commentId);
      if (result && Array.isArray(result.comments)) {
        setComments(result.comments);
        onCommentsChanged?.(result.comments);
      }
    } catch (err) {
      console.error('Error deleting comment:', err);
      setRowError('Kunde inte ta bort kommentaren');
    }
  };

  return (
    <div className="ids-mt-2">
      {confirmDeleteId != null && (
        <ConfirmDialog
          message="Vill du ta bort kommentaren?"
          onConfirm={() => handleDelete(confirmDeleteId)}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
      {comments.length > 0 && (
        <>
          <IDSButton
            size="s"
            tertiary
            icon={open ? 'chevron-down' : 'chevron-right'}
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? 'Dölj kommentarer' : 'Visa kommentarer'}
          >
            {`Kommentarer (${comments.length})`}
          </IDSButton>

          {open && (
            <div className="ids-mt-2 ids-mb-2">
              {comments.map((c, idx) => {
                const reviewer = isReviewerComment(c);
                const isEditing = editingId === c.id;
                return (
                  <div
                    key={c.id || idx}
                    className="ids-flex ids-mb-4"
                    style={{
                      flexDirection: 'column',
                      alignItems: reviewer ? 'flex-start' : 'flex-end',
                    }}
                  >
                    <IDSCard className="ids-text-max-width">
                      <CommentAvatar
                        user={reviewer ? 2 : 1}
                        label={c.authorName || roleLabelFor(c)}
                        subtitle={roleLabelFor(c)}
                      />
                      {isEditing ? (
                        <div style={{ minWidth: 260 }}>
                          <IDSInput
                            label="Redigera kommentar"
                            type="text"
                            maxLength={400}
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                          />
                          <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <IDSButton size="s" onClick={() => saveEdit(c.id)} disabled={!editText.trim()}>Spara</IDSButton>
                            <IDSButton size="s" secondary onClick={cancelEdit}>Avbryt</IDSButton>
                          </div>
                        </div>
                      ) : (
                        <div style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>{c.commentText}</div>
                      )}
                    </IDSCard>
                    <div className="ids-small ids-mt-1" style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                      <span>{formatDate(c.createdDate)}</span>
                      {!isEditing && canEdit(c) && (
                        <>
                          <button
                            type="button"
                            onClick={() => startEdit(c)}
                            title="Redigera kommentar"
                            aria-label="Redigera kommentar"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ids-palette-neutral-40)', padding: 0, font: 'inherit' }}
                          >
                            Redigera
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteId(c.id)}
                            title="Ta bort kommentar"
                            aria-label="Ta bort kommentar"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ids-palette-error-40, #c73c30)', padding: 0, font: 'inherit' }}
                          >
                            Ta bort
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              {rowError && (
                <div className="ids-mt-2">
                  <IDSAlert type="error"><p>{rowError}</p></IDSAlert>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {canAdd && (
        !showCommentField ? (
          <IDSButton size="s" secondary onClick={() => setShowCommentField(true)}>
            + Kommentar
          </IDSButton>
        ) : (
          <div style={{ maxWidth: 420 }}>
            <IDSInput
              label="Lägg till kommentar"
              type="text"
              maxLength={400}
              placeholder="Skriv en kommentar..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
            />
            <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <IDSButton size="s" onClick={handleAddComment} disabled={loading || !newComment.trim()}>Lägg till</IDSButton>
              <IDSButton size="s" secondary onClick={() => { setShowCommentField(false); setError(null); }}>Avbryt</IDSButton>
            </div>
            {error && (
              <div className="ids-mt-2">
                <IDSAlert type="error"><p>{error}</p></IDSAlert>
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
};

export default CommentThread;